import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import superadminServices from 'app/services/superadmin-services';
import { formatTimeAgo } from 'app/shared/devices/deviceDisplay';
import { CreateButton, ResponsiveDataTable, SecondaryActionButton, StandardPageHeader } from 'app/shared/ui';
import AdoptVisionDeviceDialog from './AdoptVisionDeviceDialog';
import { OnlineChip, describeError } from './visionDisplay';

// Los pendientes aparecen solos: el equipo manda su estado cada 5 minutos.
const REFRESH_INTERVAL_MS = 30 * 1000;

const Mono = ({ children }) => (
  <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>{children}</Typography>
);

const networkSummary = (device) => [
  ...(device.local_ips || []),
  device.tailscale_ip ? `${device.tailscale_ip} (Tailscale)` : null,
].filter(Boolean).join(', ') || '-';

// Equipos de Xpit AI Vision: se dan de alta solos en AWS y quedan pendientes hasta que un superadmin los adopta en una
// empresa y un local (vault: 07 Proyectos/Xpit AI Vision).
const VisionDevicesIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('state') === 'adopted' ? 'adopted' : 'pending';
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [adoptDevice, setAdoptDevice] = useState(null);

  const loadRecords = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    superadminServices.getVisionDevices(activeTab)
      .then((resp) => {
        setRecords(resp?.data || []);
        setMeta(resp?.meta || {});
      })
      .catch((error) => setNotice({ severity: 'error', text: describeError(error, 'No se pudo cargar los equipos') }))
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => {
    loadRecords();
    const timer = setInterval(() => loadRecords(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadRecords]);

  useEffect(() => {
    superadminServices.getCompanies('?page=1&include_archived=false')
      .then((resp) => setCompanies(resp?.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const openAdopt = (device = null) => {
    setAdoptDevice(device);
    setAdoptOpen(true);
  };

  const pendingColumns = [
    { key: 'device_id', label: 'ID', render: (record) => <Link component={RouterLink} to={`/superadmin/vision/${record.id}`}><Mono>{record.device_id}</Mono></Link> },
    { key: 'adoption_code', label: 'Código', render: (record) => (record.adoption_code ? <Chip size="small" variant="outlined" label={<Mono>{record.adoption_code}</Mono>} /> : '-') },
    { key: 'mac', label: 'MAC', render: (record) => <Mono>{record.mac || '-'}</Mono> },
    { key: 'ips', label: 'IP', render: networkSummary },
    { key: 'version', label: 'Sistema', render: (record) => [record.hardware, record.os_version].filter(Boolean).join(' · ') || '-' },
    { key: 'last_seen_at', label: 'Visto', render: (record) => <OnlineChip device={record} /> },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => <Button size="small" variant="contained" onClick={() => openAdopt(record)}>Adoptar</Button>,
    },
  ];

  const adoptedColumns = [
    { key: 'name', label: 'Equipo', render: (record) => <Link component={RouterLink} to={`/superadmin/vision/${record.id}`}>{record.name || record.device_id}</Link> },
    { key: 'company', label: 'Empresa · local', render: (record) => [record.company?.name, record.store?.name].filter(Boolean).join(' · ') || '-' },
    { key: 'device_id', label: 'ID', render: (record) => <Mono>{record.device_id}</Mono> },
    { key: 'status', label: 'Estado', render: (record) => <OnlineChip device={record} /> },
    {
      key: 'cameras',
      label: 'Cámaras',
      render: (record) => {
        const { total = 0, ok = 0 } = record.cameras_summary || {};
        if (!total) return '-';
        return <Chip size="small" color={ok === total ? 'success' : 'error'} variant="outlined" label={`${ok}/${total} bien`} />;
      },
    },
    { key: 'points_count', label: 'Puntos', render: (record) => record.points_count },
    { key: 'os_version', label: 'Versión', render: (record) => record.os_version || '-' },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => (
        <Button size="small" href={record.ui_url} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewOutlinedIcon fontSize="small" />}>
          Abrir interfaz
        </Button>
      ),
    },
  ];

  const changeTab = (value) => {
    setRecords([]);
    setSearchParams(value === 'adopted' ? { state: 'adopted' } : {}, { replace: true });
  };

  return (
    <Box>
      <StandardPageHeader
        title="Superadministración · Visión"
        subtitle="Jetson de Xpit AI Vision. Se dan de alta solos y quedan pendientes hasta adoptarlos en una empresa y un local."
        actions={(
          <>
            <SecondaryActionButton startIcon={<RefreshOutlinedIcon />} onClick={() => loadRecords()}>Actualizar</SecondaryActionButton>
            <CreateButton onClick={() => openAdopt(null)}>Adoptar por código</CreateButton>
          </>
        )}
      />

      {meta.settings_shadow_enabled === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          El envío de la contraseña a los equipos está apagado hasta desplegar el stack vision en AWS. Las contraseñas quedan
          pendientes y salen solas con el siguiente estado de cada equipo apenas se encienda.
        </Alert>
      )}

      <Paper sx={{ mb: 2, px: 2, borderRadius: 2 }}>
        <Tabs value={activeTab} onChange={(event, value) => changeTab(value)}>
          <Tab value="pending" label="Pendientes" />
          <Tab value="adopted" label="Adoptados" />
        </Tabs>
      </Paper>

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={activeTab === 'adopted' ? adoptedColumns : pendingColumns}
            rows={records}
            rowKey="id"
            emptyMessage={activeTab === 'adopted' ? 'No hay equipos adoptados.' : 'No hay equipos pendientes de adopción.'}
            mobileTitle={(record) => (activeTab === 'adopted' ? record.name : record.device_id)}
            mobileSubtitle={(record) => (activeTab === 'adopted'
              ? `${record.company?.name || '-'} · ${record.online ? 'En línea' : 'Sin conexión'}`
              : `Código ${record.adoption_code || '-'} · visto ${formatTimeAgo(record.last_seen_at) || '-'}`)}
            renderRowActions={(record) => (activeTab === 'adopted'
              ? <Button size="small" component={RouterLink} to={`/superadmin/vision/${record.id}`}>Ver</Button>
              : <Button size="small" variant="contained" onClick={() => openAdopt(record)}>Adoptar</Button>)}
          />
        )}
      </Paper>

      <Stack sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          En línea: su último estado tiene menos de 10 minutos. La interfaz del equipo se abre por Tailscale.
        </Typography>
      </Stack>

      <AdoptVisionDeviceDialog
        open={adoptOpen}
        onClose={() => setAdoptOpen(false)}
        device={adoptDevice}
        companies={companies}
        onAdopted={() => loadRecords(true)}
      />
    </Box>
  );
};

export default VisionDevicesIndex;
